import {ControlledInput} from '@hm/fundamental'
import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
  useFormContext
} from 'react-hook-form'

import {useTranslation} from '../../hooks/useTranslation'
import {InputSkeleton} from '../skeletons/InputSkeleton'

export interface FormTextInputProps<TFieldValues extends FieldValues = FieldValues> {
  readonlyMode: boolean
  formMethods?: UseFormReturn<TFieldValues>
  isLoading: boolean
  name: FieldPath<TFieldValues>
  labelTranslationKey: string
  autoComplete?: string
  testIdPrefix: string
  rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>
}

export const FormTextInput = <TFieldValues extends FieldValues = FieldValues>({
  readonlyMode,
  formMethods: formMethodsProp,
  isLoading,
  name,
  labelTranslationKey,
  autoComplete = 'off',
  testIdPrefix,
  rules
}: FormTextInputProps<TFieldValues>) => {
  const {t} = useTranslation()
  const formContext = useFormContext<TFieldValues>()
  const formMethods = formMethodsProp || formContext
  const errors = formMethods.formState.errors

  if (isLoading) {
    return <InputSkeleton />
  }

  const hasError = !!errors[name]?.message
  const localKey = `errorMessages.${errors[name]?.message}`
  const errorText = hasError ? t(localKey) : ''

  return (
    <Controller
      name={name}
      control={formMethods.control}
      rules={rules}
      render={({field, fieldState}) => (
        <div data-test-id={`${testIdPrefix}-${field.name}`}>
          <ControlledInput
            {...field}
            value={readonlyMode && !field.value ? '-' : field.value}
            label={t(labelTranslationKey)}
            autoComplete={autoComplete}
            isReadOnly={readonlyMode}
            error={errorText}
            isTouched={fieldState.isTouched}
          />
        </div>
      )}
    />
  )
}
